import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, X, RefreshCw, Activity } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DataHealthAlert - A persistent, global alert banner that appears when
 * critical data loss is detected in the trading engine.
 * 
 * IMPORTANT: This alert ONLY shows when:
 * 1. We ARE connected to a live feed
 * 2. AND we're actually losing ticks (ticks_received != ticks_processed)
 * 
 * When disconnected (no tokens, market closed, etc.), we do NOT show this alert
 * since no data loss is occurring - we're just not receiving data.
 */
export const DataHealthAlert = () => {
  const [healthData, setHealthData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  // Don't poll on login page
  const isLoginPage = location.pathname === '/login';

  const fetchHealth = useCallback(async () => {
    // Skip if on login page or no token
    const token = localStorage.getItem('token');
    if (isLoginPage || !token) {
      return;
    }

    try {
      const res = await fetch(`${API}/data-health`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setHealthData(data);
        
        // Only show alert if:
        // 1. Feed IS connected (is_feed_connected === true)
        // 2. AND there's actual data loss (CRITICAL_DATA_LOSS status)
        const isConnected = data.is_feed_connected === true;
        const isCriticalLoss = data.health_status === 'CRITICAL_DATA_LOSS';
        
        // Show alert only for actual data loss while connected
        if (isConnected && isCriticalLoss && !dismissed) {
          setIsVisible(true);
        } else if (!isCriticalLoss || !isConnected) {
          // Reset dismissed state when issue is resolved or disconnected
          setDismissed(false);
          setIsVisible(false);
        }
      }
    } catch (err) {
      // Silently fail - don't disrupt user experience
      console.debug('Health check failed:', err);
    }
  }, [dismissed, isLoginPage]);

  useEffect(() => {
    // Initial fetch
    fetchHealth();
    
    // Poll every 5 seconds for health status
    const interval = setInterval(fetchHealth, 5000);
    
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const handleDismiss = () => {
    setDismissed(true);
    setIsVisible(false);
  };

  if (!isVisible || !healthData) {
    return null;
  }

  const ticksLost = healthData.ticks_received - healthData.ticks_processed;
  const lossPercentage = healthData.data_loss_pct || 0;

  return (
    <div 
      className="bg-red-900/95 border-b-2 border-red-500 px-4 py-2 flex items-center justify-between gap-4 animate-pulse"
      data-testid="data-health-alert"
      role="alert"
    >
      <div className="flex items-center gap-3 flex-1">
        <div className="flex items-center justify-center w-10 h-10 bg-red-500/30 rounded-full shrink-0">
          <AlertTriangle className="w-6 h-6 text-red-400" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-bold text-red-200 text-sm uppercase tracking-wide">
              Critical Data Loss Detected
            </span>
            <Activity className="w-4 h-4 text-red-400 animate-pulse" />
          </div>
          <p className="text-red-300 text-xs mt-0.5">
            <span className="font-mono font-bold">{ticksLost.toLocaleString()}</span> ticks not processed 
            ({lossPercentage.toFixed(2)}% loss). Order flow calculations may be inaccurate.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <Link 
          to="/admin"
          className="px-3 py-1.5 bg-red-500/30 hover:bg-red-500/50 border border-red-500/50 rounded text-xs font-medium text-red-200 transition-colors flex items-center gap-1.5"
          data-testid="data-health-alert-view-details"
        >
          <RefreshCw className="w-3 h-3" />
          View Details
        </Link>
        
        <button
          onClick={handleDismiss}
          className="p-1.5 hover:bg-red-500/30 rounded transition-colors text-red-300 hover:text-red-100"
          title="Dismiss alert (will reappear if issue persists)"
          data-testid="data-health-alert-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default DataHealthAlert;
